import React, { forwardRef } from 'react';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { ENTRY_TYPE } from '../../../core/constants/financial';
import './FinancialInstallmentsPrint.scss';

// Extrato bancário imprimível. Recebe as linhas JÁ com saldo acumulado (mesmo
// cálculo da tela), o saldo de abertura e o saldo final, para o impresso bater
// exatamente com o exibido. Reaproveita o estilo de FinancialInstallmentsPrint.
const BankStatementPrint = forwardRef((props, ref) => {
  const {
    company = {},
    logoBase64,
    bankLabel,
    periodLabel,
    supplierLabel,
    saldoInicial = 0,
    saldoFinal = 0,
    rows = [],
  } = props;

  const filtersLine = [
    bankLabel ? `Banco: ${bankLabel}` : '',
    supplierLabel ? `Fornecedor: ${supplierLabel}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const entradas = rows
    .filter(({ inst }) => inst.financial_entry?.type === ENTRY_TYPE.RECEITA)
    .reduce((s, { inst }) => s + (Number(inst.value) || 0), 0);
  const saidas = rows
    .filter(({ inst }) => inst.financial_entry?.type === ENTRY_TYPE.DESPESA)
    .reduce((s, { inst }) => s + (Number(inst.value) || 0), 0);

  const emittedAt = new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div ref={ref} className="fin-print">
      <div className="fin-print-topbar">
        <div />
        <span className="fin-topbar-title">Extrato do Banco</span>
        <div />
      </div>

      <div className="fin-company-section">
        {logoBase64 && (
          <img className="fin-print-logo" src={logoBase64} alt="Logo da empresa" />
        )}
        <div className="fin-company-details">
          <div className="fin-company-main">
            <div className="fin-company-text">{company.name}</div>
            <div className="fin-company-text">{company.cnpj}</div>
            <div className="fin-company-text">
              {company.street}, N°{company.number}, {company.neighborhood}, {company.city},{' '}
              {company.state}
              {company.complement ? `, ${company.complement}` : ''}
              <br />
              CEP: {company.cep}
            </div>
            <div className="fin-company-text">
              <span>Fone: {company.telephone}</span>
              <span>WhatsApp: {company.cellphone}</span>
            </div>
          </div>

          <div className="fin-company-meta">
            <span>Período: {periodLabel}</span>
            {filtersLine && <span>Filtros: {filtersLine}</span>}
            <span className="fin-emitted">Emitido em: {emittedAt}</span>
          </div>
        </div>
      </div>

      <table className="fin-print-table">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>

        <thead>
          <tr>
            <th>Data Pagamento</th>
            <th>Descrição</th>
            <th>Banco</th>
            <th className="cell-center">Tipo</th>
            <th className="cell-right">Valor</th>
            <th className="cell-right">Saldo</th>
          </tr>
        </thead>

        <tbody>
          <tr className="row-even">
            <td>—</td>
            <td className="cell-text" colSpan={3}>
              Saldo Inicial
            </td>
            <td className="cell-right">—</td>
            <td className="cell-right">{formatCurrency(saldoInicial)}</td>
          </tr>
          {rows.map(({ inst, saldoAcumulado }, index) => {
            const isReceita = inst.financial_entry?.type === ENTRY_TYPE.RECEITA;
            return (
              <tr key={inst.id} className={index % 2 === 0 ? 'row-odd' : 'row-even'}>
                <td>{formatDateBR(inst.payment_date)}</td>
                <td className="cell-text">
                  {inst.financial_entry?.supplier_client || '—'}
                </td>
                <td className="cell-text">{inst.Bank?.name || '—'}</td>
                <td className="cell-center">{isReceita ? 'ENTRADA' : 'SAÍDA'}</td>
                <td className="cell-right">
                  {isReceita ? '+ ' : '- '}
                  {formatCurrency(inst.value)}
                </td>
                <td className="cell-right">{formatCurrency(saldoAcumulado)}</td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td className="cell-empty" colSpan={6}>
                Nenhum lançamento no período
              </td>
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr className="footer-row">
            <td className="footer-label" colSpan={4}>
              Entradas {formatCurrency(entradas)} · Saídas {formatCurrency(saidas)}
            </td>
            <td className="footer-label cell-right">Saldo Final</td>
            <td className="footer-value cell-right">{formatCurrency(saldoFinal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
});

BankStatementPrint.displayName = 'BankStatementPrint';

export default BankStatementPrint;
